import { Chip, ChipTone } from "./Chip";
import {
  HOTEL_STATE_DISPLAY,
  LOW_VOLUME_DERIVED_HINT,
  LOW_VOLUME_GENERIC_HINT,
  LOW_VOLUME_HINT,
  lowVolumeDerivedHint,
  UNDETERMINED_DISPLAY,
} from "../vocabulary";
import type { ReportedState, HotelStatusFacts } from "../types";

/** Health states carry health colors; the two gates are neutral facts
 *  (§3.2), and `Undetermined` (reported null) is a fact about our records —
 *  never colored as health. `Low volume` is neutral for the same reason as
 *  the gates: it is a classification of the business, not a verdict on the
 *  tracking, and coloring it would rank a hotel nobody has judged. */
const STATE_TONE: Record<ReportedState, ChipTone> = {
  healthy: "green",
  monitoring: "blue",
  unhealthy: "red",
  "not-yet-live": "neutral",
  "low-volume": "neutral",
  paused: "neutral",
};

/** The states whose chip carries an explainer on hover. `Low volume` gets
 *  the GENERIC one here: a bare chip has no facts, so it cannot say whether
 *  the daily pass read the hotel low or an operator placed it there —
 *  `lowVolumeHint` answers that wherever the facts are to hand. */
const STATE_HINT: Partial<Record<ReportedState, string>> = {
  "low-volume": LOW_VOLUME_GENERIC_HINT,
};

/**
 * WHICH Low volume this is, from the facts (ADR 0039) — undefined for every
 * other reported state, so a caller can hand it to the bare chip's `hint`
 * without checking first.
 *
 * - Derived (`lowVolume` set, no Low-volume override): the daily pass's own
 *   reading of a Healthy hotel. The hint carries the hotel's counts and says
 *   nothing was locked, because a reader raised on the declared category
 *   will assume otherwise.
 * - Declared (`override.state === "low-volume"`): the operator-placed
 *   category, locks and all. The override wins the label, so it wins the
 *   hint — and where the derivation ALSO reads the hotel low, the numbers
 *   are appended, since they are what an operator weighing whether to lift
 *   the category wants to know.
 */
export function lowVolumeHint(status: HotelStatusFacts): string | undefined {
  if (status.reported !== "low-volume") return undefined;
  const declared = status.override?.state === "low-volume";
  if (!declared) {
    // No Low-volume override and a reported Low volume means the DERIVED
    // reading, facts or not — so the derived hint stands even when the
    // evidence blob did not come back. The generic hint, which hedges over
    // both paths, would be less true than what we already know here.
    return status.lowVolume
      ? lowVolumeDerivedHint(status.lowVolume)
      : LOW_VOLUME_DERIVED_HINT;
  }
  if (!status.lowVolume) return LOW_VOLUME_HINT;
  const facts = status.lowVolume;
  return `${LOW_VOLUME_HINT} The daily pass reads it low as well: ${facts.bookings} and ${facts.priorBookings} qualifying bookings in the last two ${facts.windowDays}-day windows (fewer than ${facts.threshold}).`;
}

export function StateChipBare({
  state,
  hint,
}: {
  state: ReportedState | null;
  /** A hint composed from facts the caller holds (`lowVolumeHint`); the
   *  state's generic hint stands in when none is given. */
  hint?: string;
}) {
  if (state === null) {
    return <Chip tone="neutral" className="!text-content-primary">{UNDETERMINED_DISPLAY}</Chip>;
  }
  return (
    <Chip tone={STATE_TONE[state]} title={hint ?? STATE_HINT[state]}>
      {HOTEL_STATE_DISPLAY[state]}
    </Chip>
  );
}

/** The full status rendering: reported-state chip (its hover saying which
 *  Low volume it is, where that is the state), and under an admin override
 *  the computed state is ALWAYS displayed alongside (spec, Admin Status
 *  Override). */
export function StateChip({ status }: { status: HotelStatusFacts }) {
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <StateChipBare state={status.reported} hint={lowVolumeHint(status)} />
      {status.override && (
        <span className="text-[11px] text-gray-500">
          (computed{" "}
          {status.computed
            ? HOTEL_STATE_DISPLAY[status.computed]
            : UNDETERMINED_DISPLAY}
          )
        </span>
      )}
    </span>
  );
}
